import { useRef } from 'react'

// ─── Utilidades ────────────────────────────────────────────────────────────────

/** Formatea un valor como pesos chilenos sin decimales */
const formatCLP = (valor) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(Math.round(valor))

/** Formatea fecha/hora en zona horaria de Santiago */
const formatFecha = (f) =>
  new Date(f).toLocaleString('es-CL', {
    timeZone: 'America/Santiago',
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })

/** Calcula neto e IVA a partir de un total con IVA incluido (19%) */
const calcularIVA = (totalConIva, tasaIva = 0.19) => {
  const neto = Math.round(totalConIva / (1 + tasaIva))
  const iva  = Math.round(totalConIva) - neto
  return { neto, iva }
}

// ─── Estilos compartidos (preview + ventana impresión) ─────────────────────────

const CSS_TICKET = `
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    font-family: 'Courier New', monospace;
    font-size: 12px;
    color: #000;
    background: #fff;
    padding: 8px;
    width: 302px;
  }
  .sep-dash  { border: none; border-top: 1px dashed #000; margin: 8px 0; }
  .sep-solid { border: none; border-top: 2px solid  #000; margin: 8px 0; }

  /* ── Encabezado ── */
  .header        { text-align: center; padding-bottom: 8px; }
  .header h1     { font-size: 15px; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; }
  .header .sub   { font-size: 11px; margin-top: 2px; color: #333; }

  /* ── Info venta ── */
  .info-row      { display: flex; justify-content: space-between; font-size: 11px; line-height: 1.6; }
  .info-label    { color: #555; }

  /* ── Tabla productos ── */
  table          { width: 100%; font-size: 11px; border-collapse: collapse; }
  th             { text-align: left; border-bottom: 1px solid #000; padding: 2px 0; font-size: 10px; text-transform: uppercase; }
  td             { padding: 3px 0; vertical-align: top; }
  .qty           { text-align: center; }
  .price, .sub-t { text-align: right; white-space: nowrap; }
  .prod-name     { max-width: 110px; word-break: break-word; padding-right: 4px; }

  /* ── Totales ── */
  .totals        { font-size: 11px; }
  .totals .row   { display: flex; justify-content: space-between; line-height: 1.8; }
  .totals .grand { font-size: 14px; font-weight: bold; }

  /* ── Footer ── */
  .footer        { text-align: center; font-size: 10px; padding-top: 8px; line-height: 1.7; }
  .footer .brand { font-size: 9px; color: #555; margin-top: 4px; }

  @media print { body { width: auto; } }
`

// ─── Componente principal ───────────────────────────────────────────────────────

/**
 * Props:
 *  venta    – { folio, fecha, total, metodo_pago? }
 *  detalles – [{ nombre|productos.nombre, cantidad, precio_unitario|precio, subtotal }]
 *  empresa  – string  (nombre de la empresa)
 *  sucursal – string  (nombre de la sucursal, opcional)
 *  vendedor – string
 *  ivaRate  – number  (tasa IVA, ej: 0.19 — viene de BD via get_iva_rate())
 *  onCerrar – fn
 */
export default function TicketImprimible({ venta, detalles, empresa, sucursal, vendedor, ivaRate = 0.19, onCerrar }) {
  const ticketRef = useRef()

  const { neto, iva } = calcularIVA(venta.total, ivaRate)

  const imprimir = () => {
    const contenido = ticketRef.current.innerHTML
    const win = window.open('', '_blank', 'width=420,height=640')
    win.document.write(`<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>Ticket #${venta.folio}</title>
  <style>${CSS_TICKET}</style>
</head>
<body>${contenido}</body>
</html>`)
    win.document.close()
    win.focus()
    setTimeout(() => { win.print(); win.close() }, 350)
  }

  // ── Vista previa (inline styles para que funcione en el modal) ─────────────
  const previewStyle = {
    fontFamily: "'Courier New', monospace",
    fontSize: '13px',
    background: '#fff',
    color: '#000',
    padding: '16px',
    border: '1px dashed #ccc',
    borderRadius: '4px',
    maxHeight: '62vh',
    overflowY: 'auto',
    marginBottom: '1rem',
    width: '302px',
    margin: '0 auto 1rem',
  }

  return (
    <div className="modal-overlay" onClick={onCerrar}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }}>

        <div className="modal-header">
          <h2 className="modal-title">🧾 Ticket de Venta</h2>
        </div>

        {/* ── Vista previa ───────────────────────────────────────────── */}
        <div ref={ticketRef} style={previewStyle}>

          {/* Encabezado empresa */}
          <div style={{ textAlign: 'center', borderBottom: '1px dashed #000', paddingBottom: '8px', marginBottom: '8px' }}>
            <div style={{ fontSize: '15px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px' }}>
              {empresa || 'Mi Empresa'}
            </div>
            {sucursal && (
              <div style={{ fontSize: '11px', marginTop: '2px', color: '#555' }}>{sucursal}</div>
            )}
            <div style={{ fontSize: '10px', marginTop: '3px', color: '#777' }}>
              Comprobante de Venta
            </div>
          </div>

          {/* Info de la venta */}
          <div style={{ marginBottom: '8px', fontSize: '11px', lineHeight: '1.7' }}>
            {[
              ['Folio',    `#${venta.folio}`],
              ['Fecha',    formatFecha(venta.fecha)],
              ['Vendedor', vendedor],
              ...(venta.metodo_pago ? [['Pago', venta.metodo_pago]] : []),
            ].map(([label, val]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#555' }}>{label}:</span>
                <span>{val}</span>
              </div>
            ))}
          </div>

          <hr style={{ border: 'none', borderTop: '1px dashed #000', margin: '8px 0' }} />

          {/* Tabla de productos */}
          <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left',   borderBottom: '1px solid #000', padding: '2px 0', fontSize: '10px' }}>Producto</th>
                <th style={{ textAlign: 'center', borderBottom: '1px solid #000', padding: '2px 0', fontSize: '10px' }}>Cant</th>
                <th style={{ textAlign: 'right',  borderBottom: '1px solid #000', padding: '2px 0', fontSize: '10px' }}>P.Unit</th>
                <th style={{ textAlign: 'right',  borderBottom: '1px solid #000', padding: '2px 0', fontSize: '10px' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {detalles.map((d, i) => {
                const nombre    = d.nombre || d.productos?.nombre || '—'
                const cantidad  = d.cantidad
                const precio    = d.precio_unitario ?? d.precio ?? 0
                const subtotal  = d.subtotal ?? precio * cantidad
                return (
                  <tr key={i}>
                    <td style={{ padding: '3px 4px 3px 0', maxWidth: '110px', wordBreak: 'break-word' }}>{nombre}</td>
                    <td style={{ textAlign: 'center', padding: '3px 2px' }}>{cantidad}</td>
                    <td style={{ textAlign: 'right',  padding: '3px 0 3px 4px', whiteSpace: 'nowrap' }}>{formatCLP(precio)}</td>
                    <td style={{ textAlign: 'right',  padding: '3px 0',         whiteSpace: 'nowrap' }}>{formatCLP(subtotal)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          {/* Totales con desglose IVA */}
          <div style={{ borderTop: '2px solid #000', marginTop: '8px', paddingTop: '8px', fontSize: '11px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.8' }}>
              <span>Neto:</span><span>{formatCLP(neto)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.8' }}>
              <span>IVA ({Math.round(ivaRate * 100)}%):</span><span>{formatCLP(iva)}</span>
            </div>
            <hr style={{ border: 'none', borderTop: '1px dashed #000', margin: '4px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '14px' }}>
              <span>TOTAL:</span><span>{formatCLP(venta.total)}</span>
            </div>
          </div>

          {/* Footer */}
          <div style={{ textAlign: 'center', marginTop: '12px', fontSize: '10px', borderTop: '1px dashed #000', paddingTop: '8px', lineHeight: '1.7' }}>
            <p>¡Gracias por su compra!</p>
            <p>Conserve este comprobante</p>
            <p style={{ marginTop: '5px', fontSize: '9px', color: '#888' }}>
              Powered by <strong>Puntum POS</strong> · puntum.cl
            </p>
          </div>

        </div>
        {/* ── Fin vista previa ──────────────────────────────────────── */}

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onCerrar}>Cerrar</button>
          <button className="btn btn-primary" onClick={imprimir}>🖨️ Imprimir</button>
        </div>

      </div>
    </div>
  )
}